import { useSyncExternalStore } from "react";
import { Memory, evaluateNetwork, type NetworkPower } from "./ladder";
import { solveElectrical, tickDevices, type ElectricalResult } from "./electrical";
import { blankProject, demoProject } from "./defaultProject";
import { DEF } from "./library";
import { diagnose, type Issue } from "./diagnostics";
import { EXAMPLES } from "./examples";
import type { DeviceInst, Project, Wire } from "./types";

const KEY = "plcsim.projects";
const LAST = "plcsim.last";
const AUTO = "plcsim.autosave";
export const GRID = 12;

export type Status = "STOP" | "RUN";

type Listener = () => void;

class SimStore {
  project: Project = demoProject();
  status: Status = "STOP";
  mem = new Memory();
  elec: ElectricalResult = { plus: new Set(), minus: new Set(), dsu: null as never, live: new Set(), inputs: {} };
  power: Record<string, NetworkPower> = {};
  scanCount = 0;
  scanMs = 0;

  private structListeners = new Set<Listener>();
  private tickListeners = new Set<Listener>();
  private structVersion = 0;
  private tickVersion = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private last = 0;
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private autoTimer: ReturnType<typeof setTimeout> | null = null;
  private issueCache: { v: number; list: Issue[] } = { v: -1, list: [] };

  /* --------------------------------------------------------- diagnostics */
  get issues(): Issue[] {
    if (this.issueCache.v !== this.structVersion)
      this.issueCache = { v: this.structVersion, list: diagnose(this.project) };
    return this.issueCache.list;
  }

  /* ------------------------------------------------------- subscriptions */
  subscribeStruct = (l: Listener) => {
    this.structListeners.add(l);
    return () => this.structListeners.delete(l);
  };
  subscribeTick = (l: Listener) => {
    this.tickListeners.add(l);
    return () => this.tickListeners.delete(l);
  };
  getStruct = () => this.structVersion;
  getTick = () => this.tickVersion;

  private persistNow() {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(AUTO, JSON.stringify(this.project));
    } catch {
      /* quota */
    }
  }

  private emitStruct() {
    this.structVersion++;
    // Structural changes (add/remove device, wires, etc.) are persisted
    // immediately so a Vite refresh can never lose the work panel.
    this.persistNow();
    this.structListeners.forEach((l) => l());
    this.emitTick();
  }
  private emitTick() {
    this.tickVersion++;
    this.tickListeners.forEach((l) => l());
  }

  /* --------------------------------------------------------- mutations */
  update(fn: (p: Project) => void, history = true) {
    if (history) this.pushHistory();
    fn(this.project);
    this.project.updatedAt = Date.now();
    this.solveOnce();
    this.emitStruct();
  }

  /** state-only change (no history, no structural churn) */
  touch() {
    this.solveOnce();
    this.emitTick();
  }

  private pushHistory() {
    this.undoStack.push(JSON.stringify(this.project));
    if (this.undoStack.length > 60) this.undoStack.shift();
    this.redoStack = [];
  }
  undo() {
    const s = this.undoStack.pop();
    if (!s) return;
    this.redoStack.push(JSON.stringify(this.project));
    this.project = JSON.parse(s);
    this.solveOnce();
    this.emitStruct();
  }
  redo() {
    const s = this.redoStack.pop();
    if (!s) return;
    this.undoStack.push(JSON.stringify(this.project));
    this.project = JSON.parse(s);
    this.solveOnce();
    this.emitStruct();
  }

  /* ---------------------------------------------------------- autosave */
  private scheduleAutoSave() {
    if (typeof window === "undefined") return;
    if (this.autoTimer) clearTimeout(this.autoTimer);
    this.autoTimer = setTimeout(() => {
      try {
        localStorage.setItem(AUTO, JSON.stringify(this.project));
      } catch {
        /* quota */
      }
    }, 400);
  }

  /* -------------------------------------------------------------- tags */
  setTag(addr: string, name: string) {
    this.update((p) => {
      p.tags ??= {};
      if (name.trim()) p.tags[addr] = name.trim();
      else delete p.tags[addr];
    }, false);
  }
  tag(addr: string) {
    return this.project.tags?.[addr] ?? "";
  }

  setSnap(v: boolean) {
    this.project.snap = v;
    this.emitStruct();
  }
  get snap() {
    return this.project.snap !== false;
  }
  setView(v: { x: number; y: number; k: number }) {
    if (
      !Number.isFinite(v.x) ||
      !Number.isFinite(v.y) ||
      !Number.isFinite(v.k) ||
      v.k <= 0
    )
      return;
    this.project.view = {
      x: v.x,
      y: v.y,
      k: Math.min(3, Math.max(0.15, v.k)),
    };
    this.scheduleAutoSave();
  }

  loadExample(id: string) {
    const ex = EXAMPLES.find((e) => e.id === id);
    if (ex) this.load(ex.build());
  }

  /* ------------------------------------------------------------ devices */
  addDevice(type: string, x: number, y: number) {
    const def = DEF[type];
    if (!def) return;
    const id = `${type}_${Math.random().toString(36).slice(2, 7)}`;
    const count = this.project.devices.filter((d) => d.type === type).length + 1;
    const dev: DeviceInst = {
      id,
      type,
      x: Math.round(x),
      y: Math.round(y),
      label: `${def.name} ${count}`,
      state: {},
      props: type === "timer" ? { preset: "3" } : {},
    };
    this.update((p) => {
      p.devices.push(dev);
    });
    return id;
  }

  removeDevice(id: string) {
    this.update((p) => {
      p.devices = p.devices.filter((d) => d.id !== id);
      p.wires = p.wires.filter(
        (w) => !w.from.startsWith(`${id}:`) && !w.to.startsWith(`${id}:`),
      );
    });
  }

  duplicateDevice(id: string) {
    const d = this.project.devices.find((x) => x.id === id);
    if (!d) return;
    const copy: DeviceInst = {
      ...JSON.parse(JSON.stringify(d)),
      id: `${d.type}_${Math.random().toString(36).slice(2, 7)}`,
      x: d.x + 40,
      y: d.y + 40,
    };
    this.update((p) => {
      p.devices.push(copy);
    });
    return copy.id;
  }

  /** push an undo checkpoint before a drag gesture */
  beginMove() {
    this.pushHistory();
  }

  moveDevice(id: string, x: number, y: number, history = false) {
    if (history) this.pushHistory();
    const d = this.project.devices.find((x2) => x2.id === id);
    if (!d) return;

    const g = this.snap ? GRID : 1;
    const nextX = Math.round(x / g) * g;
    const nextY = Math.round(y / g) * g;
    const dx = nextX - d.x;
    const dy = nextY - d.y;

    // A manually-routed wire stores its orange routing handle in absolute
    // panel coordinates. If both ends of that wire belong to the device being
    // moved, move the handle by the exact same snapped delta. This keeps the
    // whole routed wire attached to the device, just like the electrical
    // circuit simulator, instead of leaving the orange handle behind.
    if (dx !== 0 || dy !== 0) {
      for (const w of this.project.wires) {
        if (
          w.control &&
          w.from.startsWith(`${id}:`) &&
          w.to.startsWith(`${id}:`)
        ) {
          w.control = { x: w.control.x + dx, y: w.control.y + dy };
        }
      }
    }

    d.x = nextX;
    d.y = nextY;
    this.project.updatedAt = Date.now();
    this.emitTick();
    this.scheduleAutoSave();
  }

  addWire(from: string, to: string, color: string) {
    if (from === to) return;
    const exists = this.project.wires.some(
      (w) => (w.from === from && w.to === to) || (w.from === to && w.to === from),
    );
    if (exists) return;
    const wire: Wire = { id: `w_${Math.random().toString(36).slice(2, 8)}`, from, to, color };
    this.update((p) => {
      p.wires.push(wire);
    });
  }
  removeWire(id: string) {
    this.update((p) => {
      p.wires = p.wires.filter((w) => w.id !== id);
    });
  }
  setWireColor(id: string, color: string) {
    this.update((p) => {
      const w = p.wires.find((x) => x.id === id);
      if (w) w.color = color;
    });
  }

  /** Push one undo checkpoint before dragging a wire routing handle. */
  beginWireMove() {
    this.pushHistory();
  }

  /** Move the wire's single routing handle without adding history on every pixel. */
  moveWireControl(id: string, x: number, y: number) {
    const w = this.project.wires.find((wire) => wire.id === id);
    if (!w) return;
    w.control = { x, y };
    this.project.updatedAt = Date.now();
    this.emitTick();
    this.scheduleAutoSave();
  }

  /* -------------------------------------------------------- simulation */
  solveOnce() {
    const q: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(this.mem.bits)) if (k.startsWith("Q")) q[k] = v;
    this.elec = solveElectrical(this.project, q);
    for (const [addr, v] of Object.entries(this.elec.inputs)) this.mem.setBit(addr, v);
  }

  run() {
    if (this.timer) return;
    this.status = "RUN";
    this.last = performance.now();
    this.timer = setInterval(() => this.scan(), this.project.scanMs);
    this.emitStruct();
  }
  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.status = "STOP";
    this.mem.clearNonRetentive();
    for (const d of this.project.devices) {
      d.state["coil"] = false;
      d.state["run"] = false;
      d.state["lit"] = false;
      d.state["t"] = 0;
    }
    this.power = {};
    this.solveOnce();
    this.emitStruct();
  }
  reset() {
    this.mem = new Memory();
    this.scanCount = 0;
    this.solveOnce();
    this.emitStruct();
  }
  toggleRun() {
    this.status === "RUN" ? this.stop() : this.run();
  }

  scan() {
    const now = performance.now();
    const dt = Math.min(200, now - this.last);
    this.last = now;
    const t0 = performance.now();

    // 1. read process image from the electrical panel
    this.solveOnce();
    // 2. execute the program
    for (const net of this.project.networks) {
      this.power[net.id] = evaluateNetwork(net, this.mem, dt);
    }
    // 3. write outputs back to the panel and settle the circuit
    this.solveOnce();
    tickDevices(this.project, dt);
    this.solveOnce();

    this.scanMs = performance.now() - t0;
    this.scanCount++;
    this.emitTick();
  }

  /* ---------------------------------------------------------- projects */
  save() {
    const all = this.list().filter((p) => p.id !== this.project.id);
    all.push(JSON.parse(JSON.stringify(this.project)));
    localStorage.setItem(KEY, JSON.stringify(all));
    localStorage.setItem(LAST, this.project.id);
    this.emitStruct();
  }
  list(): Project[] {
    try {
      return JSON.parse(localStorage.getItem(KEY) ?? "[]");
    } catch {
      return [];
    }
  }
  load(p: Project) {
    this.stop();
    this.project = JSON.parse(JSON.stringify(p));
    this.mem = new Memory();
    this.undoStack = [];
    this.redoStack = [];
    this.solveOnce();
    this.emitStruct();
  }
  remove(id: string) {
    localStorage.setItem(KEY, JSON.stringify(this.list().filter((p) => p.id !== id)));
    this.emitStruct();
  }
  newProject() {
    this.load(blankProject());
  }
  loadDemo() {
    this.load(demoProject());
  }
  restoreLast() {
    const auto = localStorage.getItem(AUTO);
    if (auto) {
      try {
        this.load(JSON.parse(auto) as Project);
        return;
      } catch {
        /* corrupt */
      }
    }
    const id = localStorage.getItem(LAST);
    const found = this.list().find((p) => p.id === id);
    if (found) this.load(found);
    else this.solveOnce();
  }
  exportProject() {
    const blob = new Blob([JSON.stringify(this.project, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${this.project.name}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  async importProject(file: File) {
    const p = JSON.parse(await file.text()) as Project;
    p.id = `prj_${Math.random().toString(36).slice(2, 9)}`;
    this.load(p);
  }
}

// Keep the exact same simulator store while Vite refreshes library.ts.
// Also restore the last autosaved panel after a full browser reload.
type PlcGlobalStore = typeof globalThis & { __plcSimStore?: SimStore };
const plcGlobal = globalThis as PlcGlobalStore;
const hotStore = import.meta.hot?.data?.plcSimStore as SimStore | undefined;

const reusedStore = hotStore ?? plcGlobal.__plcSimStore;
export const store: SimStore = reusedStore ?? new SimStore();

if (reusedStore) {
  // Reuse the current project/devices, but attach the newest class methods.
  Object.setPrototypeOf(store, SimStore.prototype);
} else if (typeof window !== "undefined") {
  // If Vite had to do a full page reload, bring the work panel back immediately.
  store.restoreLast();
}

plcGlobal.__plcSimStore = store;

if (import.meta.hot) {
  // Accept library.ts updates here so changing terminal X/Y does not bubble into
  // a full simulator reset. Imported ESM bindings (DEF) stay live.
  import.meta.hot.accept("./library", () => {
    store.solveOnce();
    store.touch();
  });

  // Keep the project even if this module itself is replaced.
  import.meta.hot.dispose((data) => {
    data.plcSimStore = store;
    plcGlobal.__plcSimStore = store;
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(AUTO, JSON.stringify(store.project));
      } catch {
        /* quota */
      }
    }
  });
}

export function useStruct() {
  return useSyncExternalStore(
    store.subscribeStruct,
    store.getStruct,
    () => 0,
  );
}
export function useTick() {
  return useSyncExternalStore(store.subscribeTick, store.getTick, () => 0);
}
export function useProject() {
  useStruct();
  return store.project;
}
